import {
  addDays,
  hotelMidnight,
  hotelTimeParts,
  mondayOf,
  type HotelTimeParts,
} from 'admin-panel/utils/hotel-time';

import type { DashboardChartMode } from 'admin-panel/types/dashboard';
import type { DashboardTimeframe } from 'admin-panel/utils/dashboard-timeframe';

export interface DashboardPoint {
  at: string;
  value: number;
  count?: number;
}

export interface DashboardBucket {
  key: string;
  value: number;
}

export type DashboardAggregation = 'sum' | 'ratio';

interface Totals {
  value: number;
  count: number;
}

const HOUR_IN_MS = 60 * 60 * 1000;

export function bucketDashboardPoints(
  points: DashboardPoint[],
  timeframe: DashboardTimeframe,
  {
    mode,
    aggregation = 'sum',
    now = new Date(),
  }: {
    mode: DashboardChartMode;
    aggregation?: DashboardAggregation;
    now?: Date;
  }
): DashboardBucket[] {
  const keys = bucketKeys(timeframe, mode);
  const totals = new Map<string, Totals>(
    keys.map((key) => [key, { value: 0, count: 0 }])
  );
  const averagesCompletedPeriods = mode !== 'totals' && aggregation === 'sum';
  const cutoff = averagesCompletedPeriods
    ? completedPeriodsEnd(timeframe, mode, now)
    : Infinity;

  points.forEach((point) => {
    if (Date.parse(point.at) >= cutoff) {
      return;
    }

    const parts = hotelTimeParts(point.at, timeframe.timezone);

    if (
      parts.date < timeframe.startDate ||
      parts.date >= timeframe.endDateExclusive
    ) {
      return;
    }

    const bucket = totals.get(bucketKey(parts, timeframe, mode));

    if (bucket) {
      bucket.value += point.value;
      bucket.count += point.count ?? 0;
    }
  });

  const occurrences = averagesCompletedPeriods
    ? occurrenceCounts(timeframe, mode, cutoff)
    : undefined;

  return keys.map((key) => {
    const { value, count } = totals.get(key) as Totals;

    if (aggregation === 'ratio') {
      return { key, value: count > 0 ? value / count : 0 };
    }

    const divisor = occurrences ? occurrences.get(key) || 0 : 1;

    return { key, value: divisor > 0 ? value / divisor : 0 };
  });
}

function bucketKeys(timeframe: DashboardTimeframe, mode: DashboardChartMode) {
  if (mode === 'weekday-average') {
    return ['0', '1', '2', '3', '4', '5', '6'];
  }

  if (mode === 'hourly-average' || timeframe.totalsInterval === 'hourly') {
    return Array.from({ length: 24 }, (_value, hour) => String(hour));
  }

  const keys: string[] = [];

  for (
    let date = timeframe.startDate;
    date < timeframe.endDateExclusive;
    date = addDays(date, 1)
  ) {
    const key =
      timeframe.totalsInterval === 'weekly' ? weekKey(date, timeframe) : date;

    if (keys[keys.length - 1] !== key) {
      keys.push(key);
    }
  }

  return keys;
}

function bucketKey(
  parts: HotelTimeParts,
  timeframe: DashboardTimeframe,
  mode: DashboardChartMode
) {
  if (mode === 'weekday-average') {
    return String(parts.weekday);
  }

  if (mode === 'hourly-average' || timeframe.totalsInterval === 'hourly') {
    return String(parts.hour);
  }

  return timeframe.totalsInterval === 'weekly'
    ? weekKey(parts.date, timeframe)
    : parts.date;
}

function weekKey(date: string, timeframe: DashboardTimeframe) {
  const monday = mondayOf(date);

  return monday < timeframe.startDate ? timeframe.startDate : monday;
}

function completedPeriodsEnd(
  timeframe: DashboardTimeframe,
  mode: DashboardChartMode,
  now: Date
) {
  const end = Date.parse(timeframe.endsAt);

  if (mode === 'weekday-average') {
    const today = hotelTimeParts(now, timeframe.timezone).date;

    return Math.min(end, Date.parse(hotelMidnight(today, timeframe.timezone)));
  }

  const start = Date.parse(timeframe.startsAt);
  const completedHours = Math.floor((now.getTime() - start) / HOUR_IN_MS);

  return Math.min(end, start + Math.max(0, completedHours) * HOUR_IN_MS);
}

function occurrenceCounts(
  timeframe: DashboardTimeframe,
  mode: DashboardChartMode,
  cutoff: number
) {
  const counts = new Map<string, number>();
  const increment = (key: string) =>
    counts.set(key, (counts.get(key) || 0) + 1);

  if (mode === 'weekday-average') {
    for (
      let date = timeframe.startDate;
      date < timeframe.endDateExclusive &&
      Date.parse(hotelMidnight(date, timeframe.timezone)) < cutoff;
      date = addDays(date, 1)
    ) {
      increment(String(hotelTimeParts(`${date}T12:00:00Z`, 'UTC').weekday));
    }

    return counts;
  }

  for (
    let time = Date.parse(timeframe.startsAt);
    time < cutoff;
    time += HOUR_IN_MS
  ) {
    increment(String(hotelTimeParts(time, timeframe.timezone).hour));
  }

  return counts;
}
